import { createSelector, createSlice, PayloadAction } from '@reduxjs/toolkit'
import { HYDRATE } from 'next-redux-wrapper'

import type { RootState } from '../store'
import { Recipe } from '../../types/fam-graphql'

import { actionTypes } from './types'

export type RecipesByTagState = {
  list: Recipe[] | null
  page: number
  loadedPages: number[]
  hasMorePages: boolean
  tag: string
}

//
// Initial state
//
const initialState: RecipesByTagState = {
  list: null,
  page: 1,
  loadedPages: [],
  hasMorePages: false,
  tag: '',
}

//
// Slice (Actions & Reducers)
//
const slice = createSlice({
  name: 'recipesByTag',
  initialState,
  reducers: {
    init: () => undefined,
    setRecipes: (state, action: actionTypes.setRecipes) => {
      state.list = action.payload
    },
    setHasMorePages: (state, action: actionTypes.setHasMorePages) => {
      state.hasMorePages = action.payload
    },
    setRecipesPage: (state, action: actionTypes.setRecipesPage) => {
      state.page = action.payload
    },
    appendRecipes: (state, action: actionTypes.appendRecipes) => {
      state.list = [...(state?.list ?? []), ...action.payload]
    },
    prependRecipes: (state, action: actionTypes.appendRecipes) => {
      state.list = [...action.payload, ...(state?.list ?? [])]
    },
    setLoadedPages: (state, action: PayloadAction<number[]>) => {
      state.loadedPages = action.payload?.sort()
    },
    loadRecipes: () => undefined,
    loadMoreRecipesPage: () => undefined,
    loadPreviousRecipesPage: () => undefined,
    setTag: (state, action: actionTypes.setTag) => {
      state.tag = action.payload
    },
  },
  extraReducers: {
    [HYDRATE]: (_state, action) => {
      return action.payload.recipesByTag
    },
  },
})

export const { reducer, actions } = slice

//
// Selectors
//
const root = (state: RootState) => state.recipesByTag
const list = (state: RootState) => root(state).list
const page = (state: RootState) => root(state).page
const loadedPages = (state: RootState) => root(state).loadedPages
const firstLoadedPage = createSelector([loadedPages], (p) => {
  return p?.[0]
})
const lastLoadedPage = createSelector([loadedPages], (p) => {
  return p?.[p?.length - 1]
})
const hasMorePages = (state: RootState) => root(state).hasMorePages
const hasPreviousPages = (state: RootState) => firstLoadedPage(state) > 1
const tag = (state: RootState) => root(state).tag

export const selectors = {
  list,
  page,
  loadedPages,
  firstLoadedPage,
  lastLoadedPage,
  hasMorePages,
  hasPreviousPages,
  tag,
}
